import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page, PageHero } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import { getArticleBySlug } from '../BlogPage/blogArticles';
import UnderstandingListingAnalytics from './articles/UnderstandingListingAnalytics';
import WhereToBuyUsedSkydivingGear, {
  faq as whereToBuyFaq,
} from './articles/WhereToBuyUsedSkydivingGear';
import WhenToBuyYourOwnSkydivingGear, {
  faq as whenToBuyFaq,
} from './articles/WhenToBuyYourOwnSkydivingGear';
import css from './BlogArticlePage.module.css';

const layoutAreas = `
  topbar
  main
  footer
`;

// Maps an article slug to its prose body component and optional extras. Bodies
// live here (not in the shared metadata module) so they are only bundled into
// the article chunk. Articles with a Q&A section export their faq array so the
// FAQPage JSON-LD is built from the exact same content the body renders.
const ARTICLES = {
  'understanding-listing-analytics-on-avento': { Body: UnderstandingListingAnalytics },
  'where-to-buy-used-skydiving-gear': { Body: WhereToBuyUsedSkydivingGear, faq: whereToBuyFaq },
  'when-to-buy-your-own-skydiving-gear': { Body: WhenToBuyYourOwnSkydivingGear, faq: whenToBuyFaq },
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// "2026-06-25" -> "25 June 2026"
function formatDate(iso) {
  const [year, month, day] = iso.split('-');
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1]} ${year}`;
}

// Flattens a React element tree (strings, arrays, elements) to its plain text.
function textOf(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return `${node}`;
  if (Array.isArray(node)) return node.map(textOf).join('');
  return textOf(node.props?.children);
}

function buildFaqSchema(faq) {
  return {
    '@type': 'FAQPage',
    mainEntity: faq.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: textOf(answer) },
    })),
  };
}

function buildBreadcrumbSchema(rootURL, article) {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${rootURL}/` },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: `${rootURL}/blog` },
      { '@type': 'ListItem', position: 3, name: article.hero.title },
    ],
  };
}

// Articles can supply a dedicated social image; fall back to the desktop hero.
function socialImage(article) {
  const { hero } = article;
  return (
    article.ogImage || {
      url: hero.desktopImage,
      width: hero.desktopWidth,
      height: hero.desktopHeight,
    }
  );
}

function buildArticleSchema(rootURL, article, logo) {
  return {
    '@type': 'BlogPosting',
    headline: article.hero.title,
    description: article.metaDescription,
    image: `${rootURL}${socialImage(article).url}`,
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: 'Avento',
      ...(logo ? { logo: { '@type': 'ImageObject', url: logo } } : {}),
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${rootURL}/blog/${article.slug}`,
    },
    url: `${rootURL}/blog/${article.slug}`,
  };
}

const BlogArticlePageComponent = props => {
  const { scrollingDisabled, params, staticContext } = props;
  const config = useConfiguration();
  const rootURL = config.marketplaceRootURL || '';

  const slug = params?.slug;
  const article = getArticleBySlug(slug);
  const { Body: ArticleBody, faq } = (article && ARTICLES[slug]) || {};

  if (!article || !ArticleBody) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  const { hero } = article;
  const logo = config.branding?.logoImageMobileURL;
  const og = socialImage(article);
  const ogImages = [{ url: `${rootURL}${og.url}`, width: og.width, height: og.height }];

  const schema = [
    buildArticleSchema(rootURL, article, logo),
    buildBreadcrumbSchema(rootURL, article),
    ...(faq ? [buildFaqSchema(faq)] : []),
  ];

  return (
    <Page
      title={article.metaTitle}
      description={article.metaDescription}
      author={article.author}
      published={article.datePublished}
      updated={article.dateModified}
      openGraphType="article"
      facebookImages={ogImages}
      twitterImages={ogImages}
      schema={schema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="BlogArticlePage" />
              </Topbar>

              <Main as="main" className={css.main}>
                <PageHero {...hero} />

                <section className={css.articleSection}>
                  <div className={css.container}>
                    <div className={css.byline}>
                      <a href="/blog" className={css.backLink}>
                        &larr; All articles
                      </a>
                      <span className={css.bylineMeta}>
                        By {article.author} &middot; {formatDate(article.datePublished)}
                      </span>
                    </div>
                    <ArticleBody />
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const BlogArticlePage = connect(mapStateToProps)(BlogArticlePageComponent);

export default BlogArticlePage;
